/**
 * Ensures a date/datetime string is in the backend timestamp format ('YYYY-MM-DDT00:00:00' or 'YYYY-MM-DDTHH:mm:ss').
 *
 * - DatePicker outputs 'YYYY-MM-DD' → appends 'T00:00:00'
 * - DateTimePicker outputs 'YYYY-MM-DDTHH:mm' or 'YYYY-MM-DDTHH:mm:ss' → keeps as-is (pads seconds if missing)
 * - Already formatted strings pass through unchanged
 *
 * @param value - Date or datetime string from a picker component
 * @returns Backend-compatible timestamp or undefined if input is empty
 */
export function toApiDateTime(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  // Already has time component (e.g., '2026-03-17T14:30:00' or '2026-03-17T14:30')
  if (value.includes('T')) {
    // Pad seconds if missing (DateTimePicker may output 'YYYY-MM-DDTHH:mm')
    const parts = value.split('T');
    const time = parts[1] ?? '00:00:00';
    const timeParts = time.split(':');
    if (timeParts.length === 2) {
      return `${parts[0]}T${time}:00`;
    }
    return value;
  }
  // Date only (e.g., '2026-03-17') → append midnight
  return `${value}T00:00:00`;
}

/**
 * Formats a backend timestamp for display as a short date (e.g. "17 Mar 2026").
 * Returns '-' on empty/null/undefined so table cells render a neutral
 * placeholder instead of blank.
 */
export function toDisplayDate(value: string | undefined | null): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
